<template>
    <v-app id="layout-admin">
        <v-navigation-drawer v-model="drawer" app>
            <nav-header-partial/>
            <v-divider></v-divider>
            <v-container class="mb-0" v-if="isBuyer">
                <v-select v-model="warehouseId"
                          :items="warehouses"
                          :label="$t('warehouses.warehouse')"
                          hide-details
                          :loading="isBusy"
                          :disabled="isBusy"
                          @change="onWarehouseSelect(warehouseId)"
                          item-value="id"
                          item-text="name"
                          outlined
                >
                    <template v-slot:item="{ item }">
                        <v-list-item-content>
                            {{ item.name }}
                        </v-list-item-content>
                        <v-list-item-action>
                            <v-tooltip bottom v-if="item.carrier_service !== null">
                                <template v-slot:activator="{ on }">
                                    <v-icon color="success" class="ml-3" v-on="on">
                                        mdi-truck-check
                                    </v-icon>
                                </template>
                                <span>{{ $t('warehouses.carrier_service_set') }}</span>
                            </v-tooltip>
                            <v-tooltip bottom v-else>
                                <template v-slot:activator="{ on }">
                                    <v-icon color="warning" class="ml-3" v-on="on">
                                        mdi-truck-remove
                                    </v-icon>
                                </template>
                                <span>{{ $t('warehouses.carrier_service_not_set') }}</span>
                            </v-tooltip>
                        </v-list-item-action>
                    </template>
                </v-select>
            </v-container>
            <v-list dense>
                <template v-for="(menuItemGroup, index) in menuItemGroups">
                    <template v-if="menuItemGroup.show()">
                        <v-subheader :key="`subheader-` + index">{{ menuItemGroup.group }}</v-subheader>
                        <v-list-item-group :key="`items-` + index">
                            <v-list-item v-for="(menuItem, index) in menuItemGroup.items"
                                         :key="index"
                                         :to="menuItem.to"
                                         link
                                         exact
                            >
                                <v-list-item-action>
                                    <v-icon>
                                        {{ menuItem.icon }}
                                    </v-icon>
                                </v-list-item-action>
                                <v-list-item-content>
                                    <v-list-item-title style="white-space: normal">
                                        {{ menuItem.title }}
                                    </v-list-item-title>
                                </v-list-item-content>
                                <v-tooltip right v-if="menuItem.chip && menuItem.chip.value" :disabled="!menuItem.chip.tooltip">
                                    <template v-slot:activator="{ on }">
                                        <v-list-item-icon v-on="on">
                                            <v-chip class="ma-2" :color="menuItem.chip.color" text-color="white" x-small>
                                                {{ menuItem.chip.value }}
                                            </v-chip>
                                        </v-list-item-icon>
                                    </template>
                                    <span>{{ menuItem.chip.tooltip }}</span>
                                </v-tooltip>
                            </v-list-item>
                        </v-list-item-group>
                    </template>
                </template>
            </v-list>
        </v-navigation-drawer>
        <v-navigation-drawer app clipped right v-if="showProductTypeFilters">
            <product-type-filter/>
        </v-navigation-drawer>
        <v-app-bar app dark clipped-right color="primary">
            <v-app-bar-nav-icon @click.stop="drawer = !drawer"/>
            <v-toolbar-title>{{ $t('app.name') }}</v-toolbar-title>
            <v-spacer></v-spacer>
            <select-language-component/>
            <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                    <v-btn icon v-on="on" @click="onLogout">
                        <v-icon>mdi-logout-variant</v-icon>
                    </v-btn>
                </template>
                <span>{{ $t('logout.logout') }}</span>
            </v-tooltip>
        </v-app-bar>
        <v-main>
            <snackbar-component/>
            <v-container fluid>
                <slot/>
            </v-container>
        </v-main>
        <footer-partial/>
    </v-app>
</template>

<script>
import FooterPartial from "@/partials/FooterPartial";
import NavHeaderPartial from "@/partials/NavHeaderPartial";
import SnackbarComponent from "@/components/layout/SnackbarComponent";
import SnackbarMixin from "@/mixins/SnackbarMixin";
import {API} from "@/api/api";
import {EventBus} from "@/eventBus";
import ShoppingCartMixin from "@/mixins/ShoppingCartMixin";
import SelectLanguageComponent from "@/components/layout/SelectLanguageComponent";
import WarehouseMixin from "@/mixins/WarehouseMixin";
import ProductTypeFilter from '@/components/user/products/ProductTypeFilter'
import BulkOrderMixin from '@/mixins/BulkOrderMixin'
import ProductSetMixin from "@/mixins/ProductSetMixin";

export default {
    name: 'LayoutAuthenticated',
    mixins: [SnackbarMixin, ShoppingCartMixin, ProductSetMixin, BulkOrderMixin, WarehouseMixin],
    components: {ProductTypeFilter, SelectLanguageComponent, SnackbarComponent, NavHeaderPartial, FooterPartial},
    props: {
        showProductTypeFilters: {
            type: Boolean,
            required: false,
            default: () => false
        }
    },
    data: () => ({
        isBusyWarehouses: false,
        // available warehouses
        warehouses: [],
        // selected warehouse ID
        warehouseId: null,
        // drawer
        drawer: true,
        // items in cart
        productsInCart: 0,
        totalProductsInCart: 0,
        productSetsInCart: 0
    }),
    computed: {
        isBusy() {
            return this.isBusyWarehouses;
        },
        isBuyer() {
            let roles = this.$store.getters['identity/getRoles'];
            return roles && roles.includes('ROLE_BUYER');
        },
        isApprover() {
            let roles = this.$store.getters['identity/getRoles'];
            return roles && roles.includes('ROLE_APPROVER');
        },
        isAdmin() {
            let roles = this.$store.getters['identity/getRoles'];
            return roles && roles.includes('ROLE_ADMIN');
        },
        menuItemGroups() {
            return [
                // buyer
                {
                    group: this.$t('users.buyer'),
                    show: () => this.isBuyer,
                    items: [
                        {
                            to: {name: 'home'},
                            icon: 'mdi-home',
                            title: this.$t('home.home')
                        },
                        {
                            to: {name: 'products'},
                            icon: 'mdi-tag-text',
                            title: this.$t('products.products')
                        },
                        {
                            to: {name: 'shopping-cart'},
                            icon: 'mdi-cart',
                            title: this.$t('shopping_cart.shopping_cart'),
                            chip: {
                                value: this.productsInCart > 0 && this.totalProductsInCart > 0
                                    ? "" + this.productsInCart + " / " + this.totalProductsInCart
                                    : null,
                                color: 'orange',
                                tooltip: this.$tc('shopping_cart.products_in_shopping_cart', this.productsInCart, {n: this.productsInCart})
                                    + " / "
                                    + this.$tc('shopping_cart.total_products_in_shopping_cart', this.totalProductsInCart, {n: this.totalProductsInCart}),
                            }
                        },
                        {
                            to: {name: 'bulk-orders'},
                            icon: 'mdi-broadcast',
                            title: this.$t('orders.bulk_orders'),
                            chip: {
                                value: this.productSetsInCart,
                                color: 'orange'
                            }
                        },
                        {
                            to: {name: 'orders'},
                            icon: 'mdi-archive-arrow-down',
                            title: this.$t('orders.orders_and_bulk_orders')
                        },
                        {
                            to: {name: 'bulk-orders-import'},
                            icon: 'mdi-file-upload',
                            title: this.$t('bulk_orders_import.bulk_orders_import')
                        },
                        {
                            to: {name: 'buyers'},
                            icon: 'mdi-map-marker',
                            title: this.$t('buyers.buyers')
                        }
                    ]
                },
                // approver
                {
                    group: this.$t('users.approver'),
                    show: () => this.isApprover,
                    items: [
                        {
                            to: {name: 'approval'},
                            icon: 'mdi-check-decagram',
                            title: this.$t('approval.approval')
                        },
                        {
                            to: { name: "approver-orders" },
                            icon: 'mdi-archive-arrow-down',
                            title: this.$t('orders.orders_and_bulk_orders')
                        }
                    ]
                },
                // admin
                {
                    group: this.$t('users.admin'),
                    show: () => this.isAdmin,
                    items: [
                        {
                            to: {name: 'admin'},
                            icon: 'mdi-home',
                            title: this.$t('home.home')
                        },
                        {
                            to: {name: 'users'},
                            icon: 'mdi-account-multiple',
                            title: this.$t('users.users')
                        },
                        {
                            to: {name: 'warehouses'},
                            icon: 'mdi-warehouse',
                            title: this.$t('warehouses.warehouses')
                        },
                        {
                            to: {name: 'brands'},
                            icon: 'mdi-label-multiple',
                            title: this.$t('brands.brands')
                        },
                        {
                            to: {name: 'product-types'},
                            icon: 'mdi-ab-testing',
                            title: this.$t('product_types.product_types')
                        },
                        {
                            to: {name: 'carriers'},
                            icon: 'mdi-truck-delivery',
                            title: this.$t('carriers.carriers')
                        }
                    ]
                }
            ];
        }
    },
    methods: {
        fetchWarehouses() {
            let userId = this.$store.getters['identity/getUserId'];
            if (userId === null) {
                this.snackbarError(this.$t('snackbar.identity_check_failed'));
                return;
            }

            this.isBusyWarehouses = true;
            API.get('/api/users/' + encodeURI(userId) + '/warehouses')
                .then(response => {
                    this.warehouses = response.data.map(warehouse => warehouse.warehouse);
                    this.warehouses.sort((a, b) => a.name.localeCompare(b.name));
                    // if no warehouse is selected yet and only one warehouse is assigned, assign it automatically
                    if (this.warehouseId === null && this.warehouses.length === 1) {
                        this.onWarehouseSelect(this.warehouses[0].id);
                    }
                })
                .catch(error => {
                    this.snackbarError(error);
                })
                .finally(() => {
                    this.isBusyWarehouses = false;
                });
        },
        onLogout() {
            this.$store.commit('identity/removeAuthInfo');
            this.snackbarSuccess(this.$t('snackbar.logout_success'));
            this.$router.push({name: 'login'});
        },
        onWarehouseSelect(warehouseId) {
            let userId = this.$store.getters['identity/getUserId'];
            if (userId === null) {
                this.snackbarError(this.$t('snackbar.identity_check_failed'));
                return;
            }

            this.setSelectedWarehouse(userId, warehouseId);
            location.reload();
        },
        loadWarehouseFromStore() {
            let userId = this.$store.getters['identity/getUserId'];
            if (userId === null) {
                this.snackbarError(this.$t('snackbar.identity_check_failed'));
                return;
            }

            this.warehouseId = this.$store.getters['warehouse/getWarehouseId'](userId);
        }
    },
    created() {
        if (!this.isBuyer) {
            return
        }

        this.loadWarehouseFromStore();
        this.fetchWarehouses();

        let userId = this.$store.getters['identity/getUserId'];
        if (userId === null) {
            this.snackbarError(this.$t('snackbar.identity_check_failed'));
            return;
        }

        // products
        this.productsInCart = this.getNumberOfProductsInCart(userId, this.warehouseId);
        this.totalProductsInCart = this.getTotalNumberOfProductsInCart(userId, this.warehouseId);
        // product sets
        this.productSetsInCart = this.numberOfProductSets;

        // refresh shopping cart indicator when some product is added to cart
        EventBus.$on('refresh-shopping-cart-indicator', () => {
            // products
            this.productsInCart = this.getNumberOfProductsInCart(userId, this.warehouseId);
            this.totalProductsInCart = this.getTotalNumberOfProductsInCart(userId, this.warehouseId);
            // product sets
            this.productSetsInCart = this.numberOfProductSets;
        });
    }
}
</script>
